/**
 * Text ranking in the Flow Optimizer's window (user, 12/10/2026): View tabs
 * Formula (what a good text is) · Rules (Not enough data, never reuse,
 * proven, retire a prompt group) · Config (what the next fill does), and the
 * ranked texts live on the right whatever tab is open. Part of the
 * inheritable Texts section — its source sits in the header.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode } from 'react';
import { ListChecks, Settings2, Sigma } from 'lucide-react';
import type { DropGenTextRankingConfig } from '@trafficpulse/shared';

import { getApiErrorMessage } from '@/lib/api-error';

import { useTextRanking } from '../../../../hooks/useTextRanking';
import { useDayPreview, usePlan } from '../../../../useDropGenerator';
import { HourFormulaCard } from '../../general/hour/formula';
import { InheritTag } from '../../master/InheritTag';
import { DialogSubject } from '../../optimizer-dialog/DialogSubject';
import { OptimizerBody } from '../../optimizer-dialog/OptimizerDialog';
import { notEnoughDataValue } from '../../rules/rule-model';
import { RulesCard } from '../../rules/RulesCard';
import type { ContainerEditor } from '../../state/useContainerEditor';
import { draftTextMix, splitHitTexts } from '../../../shared/texts/text-mix';
import { fillExampleInput } from './fill/fill-example';
import { FillSection } from './fill/FillSection';
import { useLeanView } from './fill/lean/use-lean-view';
import { todayFacts, todayRows } from './fill/today/kind-facts';
import { ReadingPanel } from './results/ReadingPanel';
import { RankedResults } from './results/RankedResults';

const HINT =
  "Like the Flow Optimizer: each date range has its own formula — sliders, or blocks built from sub-formulas — over the Flow Optimizer's whole metric catalog, and the ranges' shares add up to 100 %. Rates (CR, ROI, EPV…) compare texts fairly; counts (visits, conversions, revenue) mostly favour the texts sent most. The “Not enough data” rule (Rules) reads the first range.";

const toggle = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

const Notice: FC<{ children: ReactNode }> = ({ children }) => (
  <p className="m-4 rounded-lg border border-dashed border-border px-4 py-5 text-sm text-muted-foreground">{children}</p>
);

export const TextRankingContent: FC<{ editor: ContainerEditor; countryKey: string; containerId?: string | null; onClose: () => void }> = ({ editor, countryKey, containerId = null, onClose }) => {
  const { draft, creativePatch } = editor;
  const ranking = draft.creative.ranking;
  const set = (patch: Partial<DropGenTextRankingConfig>) => creativePatch({ ranking: { ...ranking, ...patch } });
  // 21/10/2026 — the Config tab's hit-again line shows and writes Hit again's own texts (a Verdicts value).
  const shown = { ...ranking, policy: draftTextMix(draft).policy ?? {} };
  const setConfig = (patch: Partial<DropGenTextRankingConfig>) => {
    if (!patch.policy) return set(patch);
    const split = splitHitTexts(patch.policy);
    editor.hitAgainPatch({ texts: split.texts });
    set({ ...patch, policy: split.policy });
  };
  const query = useTextRanking({ countryKey, campaignWiserId: draft.campaignWiserId, ranking });
  const data = query.data;
  // Phase 37 — today's real drops, so the Config tab says what the policy does right now.
  const preview = useDayPreview(countryKey);
  const plan = usePlan(countryKey);
  const ready = data && data.reading.length === 0 ? data : null;
  const rows = todayRows(preview.data, plan.data, draft, containerId);
  const today = rows ? todayFacts(rows, draftTextMix(draft), ready ? ready.texts.filter((t) => t.inPool) : []) : null;
  const lean = useLeanView(draft.creative.textGroupIds, ready, today);
  const panel = query.isError ? (
    <Notice>Ranking unavailable — {getApiErrorMessage(query.error)}</Notice>
  ) : data && data.reading.length > 0 ? (
    <div className="overflow-y-auto p-4">
      <ReadingPanel reading={data.reading} provider={data.provider} />
    </div>
  ) : data ? (
    <RankedResults data={data} loading={query.isFetching} onToggleExclude={(id) => set({ excluded: toggle(ranking.excluded, id) })} floorVisits={notEnoughDataValue(ranking.rules)} />
  ) : (
    <Notice>{query.isFetching ? 'Ranking the texts…' : 'Pick a campaign to rank its texts.'}</Notice>
  );
  return (
    <OptimizerBody
      title="Text ranking"
      subtitle={<DialogSubject editor={editor} />}
      headerAside={<InheritTag section="creative" />}
      tabs={[
        { value: 'formula', label: 'Formula', icon: Sigma, content: <HourFormulaCard formula={ranking.formula} onChange={(formula) => set({ formula })} hint={HINT} /> },
        { value: 'rules', label: 'Rules', icon: ListChecks, content: <RulesCard surface="texts" rules={ranking.rules} onChange={(rules) => set({ rules })} firstRange={ranking.formula.groups[0]} /> },
        {
          value: 'config',
          label: 'Config',
          icon: Settings2,
          content: (
            <FillSection
              ranking={shown}
              onChange={setConfig}
              aiPerDrop={draft.creative.amount}
              onAiPerDrop={(amount) => creativePatch({ amount })}
              example={ready ? fillExampleInput(shown, draft.creative, ready) : null}
              today={today}
              lean={lean}
            />
          ),
        },
      ]}
      panel={panel}
      panelFill
      panelWidthClass="lg:w-[54rem]"
      onClose={onClose}
    />
  );
};
